import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import type { ChangeSet, ChangeTarget, DraftCustomerFields, DraftInvoiceFields, DraftItemFields, InboxKind } from './inbox-drafts';

export type {
  ChangeNames,
  ChangeSet,
  ChangeTarget,
  ChangeTargetType,
  DraftCustomerFields,
  DraftInvoiceFields,
  DraftItemFields,
  InboxKind,
} from './inbox-drafts';

export interface InboxItem {
  type: InboxKind;
  id: string;
  batchId: string | null;
  date: string;
  amount: string;
  currency: string;
  /** The category or the customer, in words - or the item's / customer's name, or a change's one-line summary. */
  label: string;
  /** The category's emoji, when it has one. */
  emoji: string | null;
  description: string | null;
  /** The account it lands in, the invoice number, an item's rate and unit, a customer's GSTIN or email. */
  detail: string | null;
  /** What Claude said about this row, when it said anything. */
  proposalNote: string | null;
  createdAt: string;
  /** The ids behind `label` and `detail`, for the edit sheet to preselect. Null on an invoice. */
  categoryId: string | null;
  accountId: string | null;
  /** Who the row is to or from, as the server reads it off the description - "mahendiran". Null when it names nobody. */
  payeeKey: string | null;
  /** A change row: the record it is about, and what it proposes. */
  target?: ChangeTarget | null;
  changes?: ChangeSet | null;
  /** A draft item's or customer's fields, when the server sends them beside the summary. */
  item?: DraftItemFields | null;
  customer?: DraftCustomerFields | null;
  /**
   * An invoice row's own fields (0018), beside the summary as `item` and
   * `customer` are: whether it was recreated from another system, the
   * number it had there and where, the original attached as its source, and
   * whether its customer is still a draft. The same fields may arrive flat
   * on the row; `importedFieldsOf` reads either. Absent on a home-grown
   * draft, which is sent from the invoice page as before.
   */
  invoice?: DraftInvoiceFields | null;
  imported?: boolean | number | null;
  externalNumber?: string | null;
  sourceSystem?: string | null;
  sourceFile?: { id: string; filename: string } | null;
  /** The invoice's own number, when the server sends it apart from `detail`. */
  number?: string | null;
  /** On a deposit row: income, or capital a partner or owner put in. Absent on other kinds. */
  depositKind?: 'income' | 'capital';
}

export interface InboxBatch {
  id: string | null;
  title: string;
  /** Claude's note on the whole batch. */
  note: string | null;
  source: string;
  createdAt: string | null;
  /** The book these rows are in. */
  orgId: string;
  orgName: string;
  colour: string;
  items: InboxItem[];
}

/** One book: enough to draw its button on the Inbox. */
export interface InboxBook {
  orgId: string;
  name: string;
  colour: string;
  total: number;
}

export interface InboxResponse {
  books: InboxBook[];
  batches: InboxBatch[];
  total: number;
  /** Books that are not listed, and why. */
  caveats: string[];
}

export const inboxKey = (orgId: string | undefined) => ['inbox', orgId] as const;

/**
 * What is waiting in the Inbox - across EVERY book - shared by the page and
 * the nav badge.
 *
 * One query key, so confirming on the page drops the badge in the sidebar
 * without a second fetch being wired up by hand. Polled gently: a proposal
 * arrives from a chat window the person is not looking at, and a badge that
 * only updates on navigation is a badge that is wrong when it matters.
 *
 * `scope=all` because the Inbox is the person's, not the book's: Claude
 * drafts into whichever book it was told, and a badge that only counted the
 * book on screen would be quiet about the other one. The key still carries
 * the active org, so a switch - which clears the cache anyway - refetches.
 */
export function useInbox(enabled = true) {
  const org = useActiveOrg();
  return useQuery({
    queryKey: inboxKey(org?.id),
    queryFn: () => api.get<InboxResponse>('/inbox?scope=all'),
    enabled: !!org?.id && enabled,
    refetchInterval: 60_000,
  });
}

export function useInboxCount(): number {
  const query = useInbox();
  return query.data?.total ?? 0;
}
